import type { ElementCategory } from '../game/content.js';

const DEFAULT_TIMEOUT_MS = 10_000;
const COMBINE_TIMEOUT_MS = 30_000;

function getApiBaseUrl(): string | null {
  const value = import.meta.env.VITE_API_BASE_URL?.trim();
  if (!value) return null;
  return value.replace(/\/+$/, '');
}

export function isBackendEnabled(): boolean {
  return Boolean(getApiBaseUrl());
}

export interface RegisterResponse {
  token: string;
  nickname: string;
  inventory: string[];
  discoveries: number;
}

export interface MeResponse {
  nickname: string;
  inventory: string[];
  discoveries: number;
  first_discoveries: string[];
  quest_chapter: number;
  daily_challenge_done: boolean;
  last_combine_at: string | null;
  elements: Record<string, { tier: number; category: string; depth: number; image_url: string | null; discovered_count: number; description: string | null }>;
}

export interface CombineResponse {
  result: string;
  tier: number;
  category: ElementCategory;
  description: string | null;
  depth: number;
  is_new: boolean;
  is_first_ever: boolean;
  first_discoverer: string;
  image_url: string | null;
  discovered_count: number;
}

export class BackendError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.code === DOMException.ABORT_ERR) return true;
  if (typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError') return true;
  return false;
}

async function requestOnce<T>(path: string, init: RequestInit, timeoutMs: number): Promise<T> {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    throw new BackendError(0, 'VITE_API_BASE_URL is not configured');
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    signal: controller.signal,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {})
    }
  }).finally(() => {
    clearTimeout(timer);
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const message = isRecord(payload) && typeof payload.error === 'string'
      ? payload.error
      : `HTTP ${response.status}`;
    throw new BackendError(response.status, message);
  }

  return payload as T;
}

const MAX_RETRIES = 2;

async function request<T>(path: string, init: RequestInit, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await requestOnce<T>(path, init, timeoutMs);
    } catch (err) {
      lastError = err;
      if (err instanceof BackendError && err.status >= 400 && err.status < 500) throw err;
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

export async function registerUser(nickname: string): Promise<RegisterResponse> {
  return request<RegisterResponse>('/api/register', {
    method: 'POST',
    body: JSON.stringify({ nickname })
  });
}

export async function fetchMe(token: string): Promise<MeResponse> {
  return request<MeResponse>('/api/me', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${encodeURIComponent(token)}`
    }
  });
}

export async function combineElements(token: string, a: string, b: string): Promise<CombineResponse> {
  return request<CombineResponse>('/api/combine', {
    method: 'POST',
    body: JSON.stringify({ token, a, b })
  }, COMBINE_TIMEOUT_MS);
}

export interface RankingEntry {
  rank: number;
  nickname: string;
  value: number;
}

export interface RankingsResponse {
  discoveries: RankingEntry[];
  first_discoveries: RankingEntry[];
  max_depth: RankingEntry[];
  total_players: number;
}

export async function fetchRankings(): Promise<RankingsResponse> {
  return request<RankingsResponse>('/api/rankings', { method: 'GET' });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
